import { useEffect, useState, useCallback } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, RefreshCw, Play, Database, Trash2, Clock, CheckCircle2, XCircle, Loader2, ShieldAlert, History } from "lucide-react";
import AppShell from "@/components/AppShell";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const PANEL_ROLES = ["admin", "super_admin"];
const fmt = (v) => (v ? new Date(v).toLocaleString() : "—");
const RUN_STATUS = {
  ok: { label: "Successful", cls: "bg-emerald-100 text-emerald-700" },
  partial: { label: "Partial", cls: "bg-amber-100 text-amber-800" },
  failed: { label: "Failed", cls: "bg-red-100 text-red-700" },
  already_running: { label: "Already running", cls: "bg-blue-100 text-blue-700" },
};
const runStatus = (s) => RUN_STATUS[s] || { label: s || "—", cls: "bg-slate-100 text-slate-600" };
const triggerLabel = (t) => (String(t || "").startsWith("manual") ? "Manual" : "Automatic");
const dur = (ms) => (ms != null ? `${(ms / 1000).toFixed(1)}s` : "—");

function Stat({ label, value, testid, tone = "" }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-4 py-3" data-testid={testid}>
      <div className="text-[11px] uppercase tracking-wide text-slate-400">{label}</div>
      <div className={`mt-0.5 text-lg font-semibold ${tone || "text-[#0A0F1C]"}`}>{value}</div>
    </div>
  );
}

export default function PreviewTools() {
  const { user } = useAuth();
  const [sched, setSched] = useState(null);
  const [demo, setDemo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    try {
      const [s, d] = await Promise.all([
        api.get("/admin/scheduler/status"),
        api.get("/admin/demo/quarantine-seed/status"),
      ]);
      setSched(s.data);
      setDemo(d.data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to load preview tools");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);

  if (user && !PANEL_ROLES.includes(user.role)) return <Navigate to="/dashboard" replace />;

  const act = async (key, fn, okMsg) => {
    if (busy) return;
    setBusy(key);
    try { const { data } = await fn(); toast.success(okMsg); await load(); return data; }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Action failed"); }
    finally { setBusy(""); }
  };

  const warning = sched?.warning;

  return (
    <AppShell title="Preview Tools" subtitle="Synthetic demonstration & scheduler controls">
      <div className="space-y-6" data-testid="preview-tools-page">
        <div data-testid="preview-warning-banner"
          className="flex items-start gap-3 rounded-xl border-2 border-amber-300 bg-amber-50 px-5 py-4">
          <ShieldAlert className="mt-0.5 shrink-0 text-amber-600" size={22} />
          <div>
            <p className="font-heading text-sm font-bold uppercase tracking-wide text-amber-800">
              Preview Environment — Synthetic Demonstration Data Only
            </p>
            <p className="mt-0.5 text-sm text-amber-700">
              These tools create clearly-labelled [TEST/PREVIEW] records for demos and testing. They never
              touch production, real users/businesses, banking details, Stripe, or external providers.
            </p>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="animate-spin text-slate-400" size={26} /></div>
        ) : (
          <>
            {warning && (
              <div data-testid="scheduler-warning" className="flex items-center gap-2 rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
                <AlertTriangle size={16} /> {warning}
              </div>
            )}

            {/* Scheduler */}
            <section className="rounded-xl border border-slate-200 bg-white p-5">
              <div className="mb-4 flex items-center justify-between">
                <h2 className="font-heading text-base font-bold text-[#0A0F1C]">Reminder Scheduler</h2>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={load} data-testid="refresh-btn"><RefreshCw size={14} className="mr-1" /> Refresh</Button>
                  <Button size="sm" disabled={!!busy} onClick={() => act("run", () => api.post("/admin/scheduler/run"), "Scheduler run started")}
                    data-testid="run-scheduler-btn">
                    {busy === "run" ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Play size={14} className="mr-1" />} Run Scheduler
                  </Button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
                <Stat label="Auto-scan" testid="sched-enabled"
                  value={sched?.enabled ? `On · ${Math.round((sched.interval_seconds || 0) / 60)}m` : "Off"}
                  tone={sched?.enabled ? "text-emerald-600" : "text-slate-500"} />
                <Stat label="Current status" testid="sched-current"
                  value={sched?.locked ? "Running…" : (sched?.last_status || "idle")}
                  tone={sched?.locked ? "text-blue-600" : ""} />
                <Stat label="Next scheduled run" testid="sched-next" value={fmt(sched?.next_run_at)} />
                <Stat label="Last run" testid="sched-last" value={fmt(sched?.last_run_at)} />
                <Stat label="Last successful run" testid="sched-last-success"
                  value={fmt(sched?.last_success?.started_at)} tone="text-emerald-600" />
                <Stat label="Last failed run" testid="sched-last-failure"
                  value={sched?.last_failure ? fmt(sched.last_failure.started_at) : "None"}
                  tone={sched?.last_failure ? "text-red-600" : "text-slate-500"} />
                <Stat label="Consecutive failures" testid="sched-consec" value={sched?.consecutive_failures ?? 0}
                  tone={(sched?.consecutive_failures || 0) > 0 ? "text-red-600" : ""} />
                <Stat label="Open failures" testid="sched-openfail" value={sched?.open_failures ?? 0} />
              </div>
              {sched?.last_counts && (
                <p className="mt-3 text-xs text-slate-500" data-testid="sched-last-counts">
                  Last run — created {sched.last_counts.created}, updated {sched.last_counts.updated},
                  escalated {sched.last_counts.escalated}, resolved {sched.last_counts.resolved}.
                </p>
              )}
            </section>

            {/* Recent scheduler runs (read-only activity log) */}
            <section className="rounded-xl border border-slate-200 bg-white p-5" data-testid="scheduler-activity-log">
              <div className="mb-4 flex items-center gap-2">
                <History size={18} className="text-slate-500" />
                <h2 className="font-heading text-base font-bold text-[#0A0F1C]">Recent Scheduler Runs</h2>
                <span className="text-xs text-slate-400">(last 5 · times shown in your local time)</span>
              </div>
              {(!sched?.recent_runs || sched.recent_runs.length === 0) ? (
                <div data-testid="activity-empty" className="py-8 text-center text-sm text-slate-400">
                  No scheduler runs have executed yet.
                </div>
              ) : (
                <ul className="space-y-2">
                  {sched.recent_runs.slice(0, 5).map((run) => {
                    const s = runStatus(run.status);
                    const failCount = Array.isArray(run.failed_deals) ? run.failed_deals.length : 0;
                    return (
                      <li key={run.run_id} data-testid={`activity-row-${run.run_id}`}
                        className="rounded-lg border border-slate-100 bg-slate-50/60 px-4 py-3">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className={`rounded px-2 py-0.5 text-[11px] font-semibold ${s.cls}`}>{s.label}</span>
                          <span className="rounded bg-slate-200 px-2 py-0.5 text-[11px] font-medium text-slate-700">
                            {triggerLabel(run.triggered_by)}
                          </span>
                          <span className="flex items-center gap-1 text-xs text-slate-500" title={`UTC: ${run.started_at || ""}`}>
                            <Clock size={12} /> {fmt(run.started_at)}
                          </span>
                          {failCount > 0 && (
                            <span className="rounded bg-red-100 px-2 py-0.5 text-[11px] font-medium text-red-700">
                              {failCount} failed {failCount === 1 ? "deal" : "deals"}
                            </span>
                          )}
                          <span className="ml-auto text-xs text-slate-400" title={`UTC finished: ${run.finished_at || ""}`}>
                            {dur(run.duration_ms)}
                          </span>
                        </div>
                        <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-0.5 text-[11px] text-slate-500">
                          <span>Deals scanned: <b className="text-slate-700">{run.deals_scanned ?? 0}</b></span>
                          <span>Created: <b className="text-slate-700">{run.created ?? 0}</b></span>
                          <span>Resolved: <b className="text-slate-700">{run.resolved ?? 0}</b></span>
                          <span>Escalated: <b className="text-slate-700">{run.escalated ?? 0}</b></span>
                        </div>
                        {(run.status === "failed" || failCount > 0) && (
                          <p className="mt-1.5 text-[11px] text-red-600" data-testid={`activity-error-${run.run_id}`}>
                            {run.status === "failed"
                              ? "This run did not complete successfully. See server logs for technical details."
                              : `${failCount} ${failCount === 1 ? "deal was" : "deals were"} skipped after retries and recorded for follow-up.`}
                          </p>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>

            {/* Demo data */}
            <section className="rounded-xl border border-slate-200 bg-white p-5">
              <div className="mb-4 flex items-center justify-between">
                <h2 className="font-heading text-base font-bold text-[#0A0F1C]">Quarantine Demo Data</h2>
                <div className="flex gap-2">
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button size="sm" disabled={!!busy} data-testid="seed-demo-btn">
                        {busy === "seed" ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Database size={14} className="mr-1" />} Seed Demo
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent data-testid="seed-confirm-dialog">
                      <AlertDialogHeader>
                        <AlertDialogTitle>Seed synthetic demo data?</AlertDialogTitle>
                        <AlertDialogDescription>
                          Creates one [TEST/PREVIEW] demo deal with clean/pending/quarantined documents,
                          an active compliance hold, a blocked simulated escrow, and a critical notification.
                          Idempotent and fully reversible via Clean Up.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction data-testid="seed-confirm-btn"
                          onClick={() => act("seed", () => api.post("/admin/demo/quarantine-seed"), "Demo data seeded")}>
                          Seed demo data
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="outline" size="sm" disabled={!!busy} data-testid="cleanup-demo-btn">
                        {busy === "cleanup" ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Trash2 size={14} className="mr-1" />} Clean Up
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent data-testid="cleanup-confirm-dialog">
                      <AlertDialogHeader>
                        <AlertDialogTitle>Remove all demo data?</AlertDialogTitle>
                        <AlertDialogDescription>
                          Permanently deletes only records tagged as demo data. Real deals and data are untouched.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction data-testid="cleanup-confirm-btn"
                          onClick={() => act("cleanup", () => api.post("/admin/demo/quarantine-seed/cleanup"), "Demo data cleaned up")}>
                          Delete demo data
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
              <div className="mb-3 flex items-center gap-2 text-sm" data-testid="demo-seeded-state">
                {demo?.seeded
                  ? <><CheckCircle2 size={16} className="text-emerald-600" /> Demo deal is seeded (<span className="font-mono text-xs">{demo.deal_id}</span>)</>
                  : <><XCircle size={16} className="text-slate-400" /> No demo data currently seeded</>}
              </div>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
                <Stat label="Clean docs" testid="demo-clean" value={demo?.documents?.clean ?? 0} tone="text-emerald-600" />
                <Stat label="Pending docs" testid="demo-pending" value={demo?.documents?.pending ?? 0} tone="text-amber-600" />
                <Stat label="Quarantined docs" testid="demo-quarantined" value={demo?.documents?.quarantined ?? 0} tone="text-red-600" />
                <Stat label="Active demo holds" testid="demo-holds" value={demo?.active_holds ?? 0} tone={(demo?.active_holds || 0) > 0 ? "text-red-600" : ""} />
                <Stat label="Demo reminders" testid="demo-reminders" value={demo?.reminders ?? 0} />
              </div>
            </section>
          </>
        )}
      </div>
    </AppShell>
  );
}
